<template>
  <div class="ov-card oh">
    <div class="ov-card__head">
      <v-icon small class="ov-card__icon">mdi-timeline-clock-outline</v-icon>
      Verlauf
      <span v-if="entries.length" class="oh__count">{{ entries.length }}</span>
      <v-spacer />
      <v-btn small icon :loading="loading" title="Neu laden" @click="load"><v-icon small>mdi-refresh</v-icon></v-btn>
    </div>
    <div v-if="error" class="oh__hint oh__hint--error">{{ error }}</div>
    <div v-else-if="!entries.length && !loading" class="oh__hint">Noch keine Einträge.</div>
    <ol v-else class="oh__list">
      <li v-for="e in visibleEntries" :key="e.key" class="oh__item" :class="`oh__item--${e.tone || 'default'}`">
        <span class="oh__dot"><v-icon x-small>{{ e.icon }}</v-icon></span>
        <div class="oh__body">
          <div class="oh__title">
            {{ e.title }}
            <span v-if="e.who" class="oh__who">· {{ e.who }}</span>
            <span v-if="e.task" class="oh__task" :class="{ 'oh__task--replaced': e.task.replaced }" :title="e.task.uid">
              <img v-if="e.task.thumb" :src="e.task.thumb" alt="" />
              <v-icon v-else x-small>mdi-image-outline</v-icon>
              {{ e.task.label }}
            </span>
          </div>
          <div v-if="e.detail" class="oh__detail">{{ e.detail }}</div>
        </div>
        <time class="oh__time" :title="fullDate(e.at)">{{ shortDate(e.at) }}</time>
      </li>
    </ol>
    <button v-if="entries.length > visibleEntries.length" type="button" class="oh__more" @click="showAll = true">
      Alle {{ entries.length }} Einträge anzeigen
    </button>
  </div>
</template>

<script>
// Verlauf einer Bestellung (Stufe A, 27.10.2026): eine Zeitleiste aus allem, was wir ohnehin speichern —
// orders.events (Änderungslink), orderDate, Task-Import (importedAt/editors), Vorschau-Versand (previewSentHistory),
// Kundenfeedback, Adress-Historie, ersetzte Tasks, Fulfiller-Übergabe, Sendungen (17TRACK) und Kundenmails (mailDispatcher).
// Nur lesen, keine Schreibzugriffe. Tasks/Mails werden pro Bestellung einmal nachgeladen.
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../../../../fb";
import { mapGetters } from "vuex";
import dayjs from "dayjs";

const EVENT_LABELS = {
  edit_link_created: { title: "Änderungslink erstellt", icon: "mdi-link-variant", tone: "edit" },
  edit_link_renewed: { title: "Änderungslink erneuert", icon: "mdi-link-variant-plus", tone: "edit" },
  edit_mail_sent: { title: "Änderungslink-Mail gesendet", icon: "mdi-email-send-outline", tone: "edit" },
  edit_link_opened: { title: "Kunde hat den Änderungslink geöffnet", icon: "mdi-cursor-default-click-outline", tone: "edit" },
  edit_reminder_sent: { title: "Erinnerung zum Änderungslink gesendet", icon: "mdi-bell-ring-outline", tone: "edit" },
  edit_extended: { title: "Kunde hat die Frist verlängert", icon: "mdi-clock-outline", tone: "edit" },
  edit_submitted: { title: "Kunde hat eine Änderung eingereicht", icon: "mdi-image-edit-outline", tone: "good" },
  edit_help_requested: { title: "Kunde bittet um Hilfe bei der Änderung", icon: "mdi-hand-heart-outline", tone: "warn" },
  edit_kept_original: { title: "Kunde bleibt bei der ursprünglichen Version", icon: "mdi-check-decagram-outline", tone: "good" },
  edit_expired_restored: { title: "Änderungslink ohne Rückmeldung abgelaufen – Original wird gefertigt", icon: "mdi-timer-off-outline", tone: "warn" },
  edit_restored: { title: "Änderungslink zurückgenommen – Original wiederhergestellt", icon: "mdi-undo-variant", tone: "warn" },
  // Kundenlinks aus den Bestelldetails (04.11.2026): Statusseite mit direkt geöffneter Aktion
  customer_link_created: { title: "Kundenlink erzeugt", icon: "mdi-link-variant", tone: "edit" },
  customer_link_mail_sent: { title: "Kundenlink-Mail gesendet", icon: "mdi-email-send-outline", tone: "edit" },
  // Selbststorno über die Statusseite (02.11.2026)
  cancel_requested: { title: "Kunde hat eine Stornierung gestartet (Code per Mail)", icon: "mdi-cancel", tone: "warn" },
  cancel_ticket: { title: "Stornierungsanfrage → Ticket (nicht automatisch ausführbar)", icon: "mdi-account-alert-outline", tone: "warn" },
  cancel_done: { title: "Stornierung ausgeführt", icon: "mdi-cancel", tone: "warn" },
  cancel_restored: { title: "Stornierte Position vom Team wiederhergestellt (Erstattung in Shopify unverändert)", icon: "mdi-backup-restore", tone: "edit" },
  cancel_failed: { title: "Stornierung automatisch fehlgeschlagen → Ticket", icon: "mdi-alert-octagon-outline", tone: "warn" },
  cancel_tickets_closed: { title: "Selbststorno: unbeantwortete Support-Tickets automatisch geschlossen", icon: "mdi-ticket-confirmation-outline", tone: "edit" },
  change_request: { title: "Änderungswunsch über die Statusseite (Task wird manuell importiert)", icon: "mdi-message-text-outline", tone: "edit" },
};

const ADDRESS_LABELS = {
  validated: "Adresse automatisch geprüft",
  normalized: "Adresse normalisiert",
  failed: "Adressprüfung fehlgeschlagen",
  mail_sent: "Adress-Check-Mail an Kunden gesendet",
  mail_reminder: "Adress-Check-Erinnerung gesendet",
  customer_confirm: "Kunde hat die Adresse bestätigt",
  customer_confirm_check: "Kunde hat die Adresse bestätigt (Adress-Check)",
  customer_update: "Kunde hat die Adresse geändert",
  confirmed: "Kunde hat die Adresse bestätigt",
  mail_bounced_hard: "Mail nicht zustellbar (harter Bounce)",
  mail_bounced_soft: "Mail vorerst nicht zustellbar (weicher Bounce)",
};
const ADDRESS_STATE = { ok: { tone: "good", text: "unauffällig" }, warn: { tone: "warn", text: "Hinweise" }, problem: { tone: "warn", text: "problematisch" }, bad: { tone: "warn", text: "problematisch" } };
const WHO_LABELS = { system: "", customer: "Kunde", google: "Google", auto: "Auto-Import", autoImport: "Auto-Import" };

const toMs = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (!Number.isNaN(n) && n > 1e11) return n;
    const d = new Date(v).getTime();
    return Number.isNaN(d) ? null : d;
  }
  if (v && typeof v.toMillis === "function") return v.toMillis();
  if (v && typeof v.seconds === "number") return v.seconds * 1000;
  return null;
};

export default {
  name: "OrderHistory",
  props: { order: { type: Object, required: true } },
  data() {
    return { tasks: [], mails: [], loading: false, error: null, showAll: false, loadedFor: null };
  },
  computed: {
    ...mapGetters(["allUsers"]),
    entries() {
      const o = this.order || {};
      const out = [];
      const push = (at, e) => {
        const ms = toMs(at);
        if (!ms) return;
        const task = e.taskUid ? this.taskMeta[String(e.taskUid)] || null : null;
        out.push({ ...e, task, at: ms, key: `${e.kind || "x"}:${ms}:${out.length}` });
      };

      // Bestellung
      push(o.orderDate, { kind: "order", title: "Bestellung eingegangen", icon: "mdi-cart-outline", detail: o.isExpress ? "Express" : "" });

      // Tasks: Import, Vorschau-Versand, Feedback (welcher Task → Chip über taskUid)
      this.tasks.forEach((t) => {
        if (!t) return;
        const taskUid = String(t.uid);
        if (t.importedAt) {
          push(t.importedAt, { kind: "import", taskUid, title: "Task importiert", icon: "mdi-import", who: this.userName(t.editors && t.editors.IMPORTED, t.importedVia) });
        }
        const cd = t.customizationData || {};
        const sent = Array.isArray(cd.previewSentHistory) && cd.previewSentHistory.length ? cd.previewSentHistory : cd.previewSent ? [cd.previewSent] : [];
        sent.forEach((s, n) => {
          push(s.at, { kind: "preview", title: n === 0 ? "Vorschau an Kunden gesendet" : `Neue Vorschau gesendet (${n + 1}.)`, icon: "mdi-email-send-outline", tone: "good", taskUid, detail: s.variant && s.variant !== "DEFAULT" ? `Variante ${s.variant}` : "" });
        });
        // Feedback: aktueller Stand + archivierte Stände (feedbackHistory via onTaskUpdates, feedbackBeforeEdit beim Parken) — je Zeitstempel einmal
        const seenFb = new Set();
        const allFb = [t.feedback, t.feedbackBeforeEdit, ...(Array.isArray(t.feedbackHistory) ? t.feedbackHistory : [])].filter((f) => f && f.timestamp);
        allFb.forEach((f) => {
          const ts = toMs(f.timestamp);
          if (!ts || seenFb.has(ts)) return;
          seenFb.add(ts);
          const up = f.satisfied === true;
          const clip = (c) => (c.length > 160 ? c.slice(0, 157) + "…" : c);
          const comment = (f.commentOriginal || f.comment || "").replace(/\s+/g, " ").trim();
          push(ts, {
            kind: "feedback",
            taskUid,
            title: up ? "Kunde: Daumen hoch" : "Kunde: Daumen runter",
            icon: up ? "mdi-thumb-up-outline" : "mdi-thumb-down-outline",
            tone: up ? "good" : "warn",
            detail: comment ? `„${clip(comment)}“` : "",
          });
          // Spätere Kommentare zum selben Daumen (comments[] ist additiv)
          (Array.isArray(f.comments) ? f.comments : []).forEach((c) => {
            const cts = toMs(c.timestamp);
            if (!cts || seenFb.has(cts) || Math.abs(cts - ts) < 2000) return;
            seenFb.add(cts);
            const txt = String(c.original || c.translated || "").replace(/\s+/g, " ").trim();
            if (!txt || txt === comment) return;
            push(cts, { kind: "feedback", taskUid, title: "Kunde hat einen Kommentar ergänzt", icon: "mdi-comment-text-outline", tone: "warn", detail: `„${clip(txt)}“` });
          });
        });
      });

      // Vom Kunden ersetzte Tasks (vor den events, deshalb nur dort, wo kein edit_submitted-Event dieselbe Minute abdeckt)
      const events = Array.isArray(o.events) ? o.events : [];
      const submittedAts = events.filter((e) => e.type === "edit_submitted").map((e) => toMs(e.at)).filter(Boolean);
      (o.artworkChangedByCustomer || []).forEach((c) => {
        const ms = toMs(c.at);
        if (ms && submittedAts.some((s) => Math.abs(s - ms) < 60000)) return;
        push(c.at, { kind: "replaced", taskUid: c.to || c.from, title: "Kunde hat eine Änderung eingereicht", icon: "mdi-image-edit-outline", tone: "good", detail: this.changeDetail(c) });
      });

      // Änderungslink-Ereignisse
      events.forEach((e) => {
        const meta = EVENT_LABELS[e.type] || { title: e.type, icon: "mdi-circle-small" };
        const parts = [];
        if (e.type === "edit_submitted") parts.push(this.changeDetail(e));
        if (e.expiresAt && ["edit_link_created", "edit_link_renewed", "edit_extended"].includes(e.type)) parts.push(`gültig bis ${this.fullDate(e.expiresAt)}`);
        if (e.to && (e.type === "edit_mail_sent" || e.type === "edit_reminder_sent" || e.type === "customer_link_mail_sent")) parts.push(`an ${e.to}`);
        if (e.type === "edit_mail_sent" && e.mode === "product") parts.push("nur Größe/Ausführung");
        if (e.type === "customer_link_created" || e.type === "customer_link_mail_sent") {
          parts.push({ status: "Statusseite", cancel: "Stornieren", address: "Adresse ändern", contact: "Nachricht" }[e.kind] || e.kind || "");
          if (e.feeWaived) parts.push("ohne Gebühr");
        }
        if (e.note) parts.push(e.note);
        push(e.at, { kind: "event", ...meta, taskUid: e.taskUid || null, who: e.by ? this.userName(e.by) : "", detail: parts.filter(Boolean).join(" · ") });
      });

      // Adresse
      (o.addressHistory || []).forEach((h) => {
        const bounced = !!(h.action && h.action.startsWith("mail_bounced"));
        const st = h.action === "validated" ? ADDRESS_STATE[h.state] : null;
        const isMail = h.action === "mail_sent" || h.action === "mail_reminder";
        push(h.at, {
          kind: "address",
          title: (ADDRESS_LABELS[h.action] || `Adresse: ${h.action || "geändert"}`) + (st ? ` – ${st.text}` : ""),
          icon: bounced ? "mdi-email-alert-outline" : isMail ? "mdi-email-check-outline" : h.action === "validated" ? "mdi-map-marker-check-outline" : "mdi-map-marker-outline",
          tone: bounced ? "warn" : st ? st.tone : h.action === "failed" ? "warn" : "default",
          who: h.by && h.by !== "customer" && h.by !== "system" && h.by !== "google" ? this.userName(h.by) : "",
          detail: [h.note || "", h.suggestion ? `Vorschlag: ${h.suggestion}` : ""].filter(Boolean).join(" · "),
        });
      });

      // Fulfiller-Übergabe (je Fulfiller, sonst fulfillDate)
      let anyFulfill = false;
      Object.entries(o.fulfillment || {}).forEach(([fulfiller, list]) => {
        (Array.isArray(list) ? list : []).forEach((f) => {
          if (!f || !f.creationDate) return;
          anyFulfill = true;
          const n = (f.fulfillItems || []).length;
          const itemTasks = [...new Set((f.fulfillItems || []).map((it) => it && it.taskId).filter(Boolean))];
          push(f.creationDate, { kind: "fulfill", taskUid: itemTasks.length === 1 ? itemTasks[0] : null, title: `An ${fulfiller} übergeben`, icon: "mdi-factory", detail: [n ? `${n} Artikel` : "", f.fulfiller_id ? `Auftrag ${f.fulfiller_id}` : "", f.success === false ? "fehlgeschlagen" : ""].filter(Boolean).join(" · "), tone: f.success === false ? "warn" : "default" });
        });
      });
      if (!anyFulfill && o.fulfillDate) push(o.fulfillDate, { kind: "fulfill", title: "An Fulfiller übergeben", icon: "mdi-factory" });

      // Sendungen (17TRACK)
      Object.entries(o.shipments || {}).forEach(([tracking, s]) => {
        if (!s) return;
        const label = `${s.carrier || "Paket"} ${tracking}`;
        if (s.firstEventAt) push(s.firstEventAt, { kind: "ship", title: "Paket unterwegs", icon: "mdi-truck-fast-outline", detail: label });
        const evs = Array.isArray(s.events) ? s.events : [];
        const delivered = evs.find((e) => e.stage === "Delivered");
        if (delivered) push(delivered.at, { kind: "ship", title: "Zugestellt", icon: "mdi-package-variant-closed", tone: "good", detail: [label, delivered.location].filter(Boolean).join(" · ") });
        const pickup = evs.find((e) => e.stage === "AvailableForPickup");
        if (pickup && !delivered) push(pickup.at, { kind: "ship", title: "Liegt zur Abholung bereit", icon: "mdi-store-outline", tone: "warn", detail: [label, pickup.location].filter(Boolean).join(" · ") });
        if (s.issue) {
          const issueAt = s.issue.at || s.updatedAt;
          push(issueAt, { kind: "ship", title: "Zustellproblem gemeldet", icon: "mdi-alert-outline", tone: "warn", detail: [label, s.issue.text || s.issue.subStatus || ""].filter(Boolean).join(" · ") });
        }
      });

      // Kundenmails (mailDispatcher, seit 23.10.2026)
      this.mails.forEach((m) => {
        const state = (m.delivery && m.delivery.state) || "";
        const bounce = m.delivery && m.delivery.bounce;
        const failed = state === "ERROR" || (bounce && bounce.kind === "hard");
        const subject = (m.message && m.message.subject) || (m.meta && m.meta.templateId) || "Mail";
        push(m.createdAt, {
          kind: "mail",
          taskUid: (m.meta && m.meta.taskUid) || null,
          title: failed ? "Mail nicht zugestellt" : state === "SUCCESS" ? "Mail gesendet" : `Mail (${state.toLowerCase() || "offen"})`,
          icon: failed ? "mdi-email-remove-outline" : "mdi-email-outline",
          tone: failed ? "warn" : "muted",
          detail: [subject, m.to ? `an ${Array.isArray(m.to) ? m.to.join(", ") : m.to}` : ""].filter(Boolean).join(" · "),
        });
      });

      // Ergebnis-Mail aus Zeiten vor dem Dispatcher (sonst doppelt)
      if (o.resultsSentDate && !this.mails.some((m) => m.meta && m.meta.source === "mailResults")) {
        push(o.resultsSentDate, { kind: "mail", title: "Vorschau-/Ergebnis-Mail gesendet", icon: "mdi-email-outline", tone: "muted" });
      }

      return out.sort((a, b) => b.at - a.at);
    },
    /** uid → { label, thumb, replaced }: Nummer nach Reihenfolge der uids (…_0 = Task 1), Vorschaubild des Tasks */
    taskMeta() {
      const meta = {};
      const list = this.tasks.filter(Boolean);
      list.forEach((t, i) => {
        const name = t.printVariantName || t.printVariant || "";
        meta[String(t.uid)] = {
          uid: String(t.uid),
          label: [list.length > 1 ? `Task ${i + 1}` : "Task", name].filter(Boolean).join(" · ") + (t.replacedBy ? " (ersetzt)" : ""),
          thumb: t.thumb || t.resultThumbnailUrl || t.originalImageThumb || t.mockupImage || (t.customizationData && t.customizationData.thumb) || null,
          replaced: !!t.replacedBy,
        };
      });
      return meta;
    },
    visibleEntries() {
      return this.showAll ? this.entries : this.entries.slice(0, 12);
    },
  },
  watch: {
    "order.uid": { immediate: true, handler: "load" },
  },
  methods: {
    userName(by, via) {
      if (!by) return via === "auto" || via === "autoImport" ? "Auto-Import" : "";
      if (typeof by === "object") return by.name || this.userName(by.uid);
      if (by in WHO_LABELS) return WHO_LABELS[by];
      const u = (this.allUsers || []).find((x) => x.uid === by);
      if (u && u.username) return u.username;
      return String(by).length >= 20 ? String(by).slice(0, 6) + "…" : String(by);
    },
    changeDetail(c) {
      const parts = [];
      if (c.sameArtwork) parts.push("gleiches Bild, nur Produkt/Extras");
      if (c.productChange) parts.push(typeof c.productChange === "string" ? c.productChange : JSON.stringify(c.productChange));
      if (c.total != null && Number(c.total) !== 0) parts.push(`${Number(c.total) > 0 ? "Aufpreis" : "Gutschrift"} ${Math.abs(Number(c.total)).toFixed(2)} ${c.currency || ""}`.trim());
      return parts.join(" · ");
    },
    shortDate(ms) {
      const d = dayjs(ms);
      return d.isSame(dayjs(), "year") ? d.format("DD.MM. HH:mm") : d.format("DD.MM.YY HH:mm");
    },
    fullDate(ms) {
      return ms ? dayjs(ms).format("DD.MM.YYYY HH:mm") : "";
    },
    async load() {
      const o = this.order;
      if (!o || !o.uid) return;
      this.loading = true;
      this.error = null;
      try {
        // Tasks: importiert in tasks/, noch nicht importierte in shopifyTasks/ (Lesen dort nur mit Recht → still überspringen)
        const loadTask = async (uid) => {
          let snap = await getDoc(doc(collection(db, "tasks"), uid)).catch(() => null);
          if (!snap || !snap.exists()) snap = await getDoc(doc(collection(db, "shopifyTasks"), uid)).catch(() => null);
          return snap && snap.exists() ? { uid, ...snap.data() } : null;
        };
        // Alle Tasks, die je zur Bestellung gehörten: aktuelle + ersetzte (Änderungslink) + geparkte Vorgänger
        const uids = new Set((o.tasks || []).map((t) => String(t.taskUid)));
        (o.artworkChangedByCustomer || []).forEach((c) => { if (c.from) uids.add(String(c.from)); if (c.to) uids.add(String(c.to)); });
        (o.events || []).forEach((e) => ["taskUid", "previousTaskUid", "placeholderTaskUid"].forEach((k) => { if (e[k]) uids.add(String(e[k])); }));
        const loaded = new Map();
        const queue = [...uids];
        while (queue.length) {
          const uid = queue.shift();
          if (loaded.has(uid)) continue;
          const t = await loadTask(uid);
          loaded.set(uid, t);
          const prev = t && t.awaitingCustomerEdit && t.awaitingCustomerEdit.previousTaskUid;
          if (prev && !loaded.has(String(prev))) queue.push(String(prev));
          if (t && t.replacedBy && !loaded.has(String(t.replacedBy))) queue.push(String(t.replacedBy));
        }
        const tasks = [...loaded.values()].filter(Boolean).sort((a, b) => String(a.uid).localeCompare(String(b.uid), undefined, { numeric: true }));
        // Mails zur Bestellung (einfacher Gleichheitsfilter, Sortierung clientseitig – kein Composite-Index nötig)
        const mailSnap = await getDocs(query(collection(db, "mailDispatcher"), where("meta.orderUid", "==", String(o.uid)), limit(60))).catch(() => null);
        this.tasks = tasks;
        this.mails = mailSnap ? mailSnap.docs.map((d) => d.data()) : [];
        this.loadedFor = o.uid;
      } catch (e) {
        this.error = e.message || String(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.oh {
  &__count {
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    background: #7b5cd6;
    border-radius: 999px;
    padding: 1px 8px;
  }
  &__hint {
    font-size: 13px;
    color: #888;
    &--error {
      color: #c0392b;
    }
  }
  &__list {
    list-style: none;
    margin: 0;
    padding: 0 0 0 4px;
    position: relative;
    &::before {
      content: "";
      position: absolute;
      left: 13px;
      top: 8px;
      bottom: 8px;
      width: 2px;
      background: #ece9f5;
    }
  }
  &__item {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: start;
    padding: 6px 0;
    position: relative;
    &--good .oh__dot {
      background: #e6f6ec;
      color: #2e9e5b;
    }
    &--warn .oh__dot {
      background: #fdf0e6;
      color: #d9731a;
    }
    &--edit .oh__dot {
      background: #efeafd;
      color: #7b5cd6;
    }
    &--muted {
      .oh__dot {
        background: #f4f4f6;
        color: #9a98a6;
      }
      .oh__title {
        color: #6f6d7a;
        font-weight: 500;
      }
    }
  }
  &__dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #f1effa;
    color: #5b5870;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    .v-icon {
      color: inherit !important;
      font-size: 13px !important;
    }
  }
  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #282828;
    line-height: 1.35;
  }
  &__who {
    font-weight: 400;
    color: #7b5cd6;
  }
  &__task {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-left: 6px;
    padding: 1px 7px 1px 2px;
    border-radius: 999px;
    background: #f3f1fa;
    color: #5b5870;
    font-size: 11px;
    font-weight: 600;
    vertical-align: middle;
    white-space: nowrap;
    img {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      object-fit: cover;
      background: #e9e6f3;
    }
    .v-icon {
      font-size: 13px !important;
      margin-left: 3px;
    }
    &--replaced {
      background: #f4f4f4;
      color: #8a8894;
      img { filter: grayscale(1); opacity: 0.8; }
    }
  }
  &__detail {
    font-size: 12px;
    color: #777;
    line-height: 1.35;
    word-break: break-word;
  }
  &__time {
    font-size: 12px;
    color: #999;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    padding-top: 2px;
  }
  &__more {
    margin-top: 8px;
    border: 0;
    background: none;
    color: #7b5cd6;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 0;
  }
}
</style>
